import { useCallback, useMemo, useState } from 'react';
import { Group, Stack, Switch, createStyles } from '@mantine/core';
import type { ZodTypeAny } from 'zod';
import { z } from 'zod';

import dt from '~/domains/contest/domains/managev2/domains/common/testing.const';
import { Text } from '~/domains/design-system/Text';
import { toSafeMarkup } from '~/domains/common/utils/toSafeMarkup';

import type { BaseInterativeField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import { FieldMeta } from '../common/FieldMeta';
import useHandleTextClick from '../../../hooks/useHandleTextClick';

export type SwitchFieldType = BaseInterativeField & {
  type: 'SwitchField';
  isDisabled?: boolean;
  isSelected?: boolean;
  shouldValidateOnBoolean?: boolean;
  id?: string;
  withoutBorder?: boolean;
};

const useStyles = createStyles((theme) => ({
  card: {
    width: '100%',
    position: 'relative',

    '&.withBorder': {
      paddingLeft: theme.other.spacing._12,
      paddingRight: theme.other.spacing._12,
      paddingTop: theme.other.spacing._8,
      paddingBottom: theme.other.spacing._8,
      borderRadius: theme.other.spacing._4,
      border: `1px solid ${theme.colors.gray[3]}`,
    },
  },
  overlay: {
    borderRadius: '4px !important',
    background: theme.white,
    width: '100%',
    height: '100%',
    position: 'absolute',
    left: 0,
    top: 0,
    opacity: 0.55,
  },
  tooltip: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    cursor: 'not-allowed',
  },
  tooltipLabel: {
    color: theme.colors.gray2[9],
    paddingTop: theme.other.spacing._8,
    paddingBottom: theme.other.spacing._8,
    paddingLeft: theme.other.spacing._16,
    paddingRight: theme.other.spacing._16,
    background: theme.colors.gray2[0],
    border: `1px solid ${theme.colors.gray2[2]}`,
    borderRadius: theme.radius.sm,
  },
  justify: {
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
  },
  description: {
    color: theme.colors.gray2[3],
    '& a': {
      color: theme.colorScheme === 'dark' ? theme.colors.splash[5] : theme.colors.splash[7],
      textDecoration: 'none',

      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  track: {
    'input:disabled:checked + &': {
      backgroundColor: theme.colors.blue[6],
      borderColor: theme.colors.blue[6],
    },
  },
  thumb: {
    'input:disabled + * > &': {
      backgroundColor: theme.white,
      borderColor: theme.colors.gray[3],
    },
  },
}));

function SwitchFieldElement({
  field,
  value,
  onValueChange,
  errors,
  isTouched,
}: StaticFieldProps<SwitchFieldType>) {
  const [hover, setHover] = useState(false);
  const { classes, theme, cx } = useStyles();
  const handleOnChange = useCallback(
    (e) => {
      if (field?.isDisabled) return;
      const { target } = e;

      if (field?.id) {
        onValueChange({
          checked: target.checked,
          id: field?.id,
        });
      } else {
        onValueChange(target.checked);
      }
    },
    [onValueChange, field?.id]
  );
  const handleTextClick = useHandleTextClick();

  const checked = useMemo(() => {
    if (field?.isSelected) return true;
    if (typeof value === 'boolean') return value;
    if (typeof (value as { checked: boolean })?.checked === 'boolean')
      return (value as { checked: boolean })?.checked;
    return false;
  }, [field?.isSelected, value]);

  return (
    <>
      <div
        className={cx(classes.card, { withBorder: !field?.withoutBorder })}
        onBlur={() => setHover(false)}
        onFocus={() => setHover(!hover)}
        onClick={() => setHover(!hover)}
        onKeyDown={() => setHover(!hover)}
        onMouseLeave={() => setHover(false)}
        onMouseEnter={() => setHover(true)}
        role="button"
        tabIndex={0}
      >
        <FieldMeta noPadding hideLabel field={field} errors={errors} isTouched={isTouched} />
        <Group data-test-id={dt.switchOption} className={classes.justify}>
          <Stack spacing={theme.other.spacing._8}>
            <Text data-test-id={dt.fieldMeta.title} variant="title-medium">
              {field.label}
            </Text>
            {field.description && (
              <Text
                data-test-id={dt.fieldMeta.description}
                className={classes.description}
                variant="body-large"
                dangerouslySetInnerHTML={{ __html: toSafeMarkup(field.description) }}
                onClick={handleTextClick}
              />
            )}
          </Stack>

          <Switch
            disabled={field?.isDisabled}
            size="md"
            checked={checked}
            onChange={handleOnChange}
            classNames={field?.locked ? { track: classes.track, thumb: classes.thumb } : undefined}
          />
        </Group>
        {field?.locked ? (
          <>
            <div className={classes.overlay} />
            {hover ? (
              <div className={classes.tooltip}>
                <Text className={classes.tooltipLabel} variant="body-medium">
                  {field?.locked}
                </Text>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
      {field?.subLabel ? (
        <Text
          component="div"
          mt={theme.other.spacing._8}
          c="gray2.3"
          variant="body-small"
          className={classes.description}
          dangerouslySetInnerHTML={{ __html: toSafeMarkup(field.subLabel) }}
          onClick={handleTextClick}
        />
      ) : null}
    </>
  );
}

const createValidationSchema = (field: SwitchFieldType) => {
  let fieldSchema: ZodTypeAny = z.union([
    z.boolean(),
    z.object({ checked: z.boolean(), id: z.string().optional() }),
  ]);

  if (field.validation?.isOptional) {
    fieldSchema = fieldSchema.optional();
  }

  return fieldSchema;
};

const Descriptor: FieldDescriptor<SwitchFieldType> = {
  FieldComponent: SwitchFieldElement,
  name: 'SwitchField',
  createValidationSchema,
};

export default Descriptor;
